"use client";

import { useRef, useState } from "react";
import { Loader2 } from "@/components/icons";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { formatRut, isValidRut } from "@/lib/utils/rut";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ViewHeader } from "./ViewHeader";
import {
  loginErrorClasses,
  loginFieldClasses,
  loginLabelClasses,
  loginPrimaryButtonClasses,
  loginQuietButtonClasses,
} from "./styles";
import type { ViewProps } from "./types";

export function ForgotPasswordView({ setView }: ViewProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rut, setRut] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nuevoError = !rut ? t("login.required") : !isValidRut(rut) ? t("login.rutInvalid") : "";
    if (nuevoError) {
      setError(nuevoError);
      inputRef.current?.focus();
      return;
    }
    setIsLoading(true);
    // Envío simulado (prototipo sin backend).
    setTimeout(() => setView("forgot_step2"), 1000);
  }

  return (
    <div className="flex w-full flex-col">
      <ViewHeader title={t("login.forgotTitle")} subtitle={t("login.forgotSubtitle")} />

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Label htmlFor="forgot-rut" className={loginLabelClasses}>
            {t("login.rutLabel")}
          </Label>
          <Input
            ref={inputRef}
            type="text"
            id="forgot-rut"
            autoComplete="username"
            autoFocus
            value={rut}
            onChange={(e) => {
              setRut(formatRut(e.target.value));
              setError("");
            }}
            placeholder={t("login.rutPlaceholder")}
            disabled={isLoading}
            aria-invalid={!!error}
            aria-describedby={error ? "forgot-rut-error" : undefined}
            className={loginFieldClasses(!!error)}
          />
          {error && (
            <p id="forgot-rut-error" className={loginErrorClasses}>
              {error}
            </p>
          )}
        </div>

        <Button type="submit" disabled={isLoading} aria-busy={isLoading} className={loginPrimaryButtonClasses}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t("login.sending")}</span>
            </>
          ) : (
            t("login.sendInstructions")
          )}
        </Button>
      </form>

      <Button
        type="button"
        variant="ghost"
        onClick={() => setView("login")}
        className={cn(loginQuietButtonClasses, "mx-auto mt-4")}
      >
        {t("login.backToLogin")}
      </Button>
    </div>
  );
}
